import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  InputBase,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import Cookies from 'universal-cookie';
import DocumentScannerIcon from '@mui/icons-material/DocumentScanner';
import ImageIcon from '@mui/icons-material/Image';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CloseIcon from '@mui/icons-material/Close';
import { tokens } from '../../global/theme/tokens';
import usePersistedState from '../../global/usePersistedState';
import { hasPermissions } from '../../auth/hasPermissions';
import { API_GET, useConfig } from '../../api/api';
import { numberToString } from '../special/numberConverter';
import { request } from './aiChatApi';
import { useAiChatSession } from './useAiChatSession';
import {
  isAcknowledgementError,
  useAiChatAcknowledgement,
} from './useAiChatAcknowledgement';
import { AiChatMessageList } from './AiChatMessageList';
import { AiChatComposer } from './AiChatComposer';
import { AiChatAcknowledgementGate, AiChatNoticeLine } from './AiChatNotice';
import {
  POSITION_SCAN_PURPOSE,
  allProposed,
  latestProposal,
  markupPrice,
  positionField,
  proposalImageIds,
  selectedProposed,
  toDocumentPositions,
  toggleProposed,
} from './positionScanProposal';

const NO_REFERENCES = new Set();

// the chat parts are centered with a maximum width, like the panel in fullscreen
const CHAT_CONTENT = 'max-w-3xl mx-auto w-full';

// a markup typed by the user: "12,5" or "12.5" in percent, null if not a number
const parseRate = (value) => {
  if (value === null || value === undefined || String(value).trim() === '')
    return null;
  const rate = Number(String(value).trim().replace(',', '.'));
  return Number.isFinite(rate) ? rate : null;
};

/**
 * "Scan document with AI" of the positions table: a fresh AI chat opened
 * for one offer, order or invoice form (backend purpose position_scan).
 *
 * Two phases. Scan: the user attaches documents and presses Scan; the
 * model reads them, asks questions through the usual cards when needed
 * and hands the positions over with propose_positions. Review: the latest
 * proposal is shown as a table where every position is selected by
 * default; the user deselects what they do not want, adds the selection
 * to the form (onAddPositions, mapped by positionScanProposal.js), cancels
 * (onCancel) or asks the AI for changes, which replaces the proposal. A
 * markup and a special markup column take rates per position (the
 * instance's default markup for all of them with one click; both add up);
 * the document's price stays as the purchase price of every line so the
 * sales price can be recalculated later.
 *
 * document: 'offer' | 'order' | 'invoice'
 * project: the form's project ({ id, name }), for the project folder picker
 * defaultTaxRate: the form's VAT rate for positions without one
 */
export const AiPositionScan = ({
  document,
  project = null,
  defaultTaxRate = 19,
  onAddPositions,
  onCancel,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();
  const config = useConfig();
  const currentUserId = new Cookies().get('userId');
  const composerRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);
  const [adding, setAdding] = useState(false);
  // the pictures of the proposal by file id, loaded when a proposal arrives
  const [images, setImages] = useState({});
  const [selected, setSelected] = useState(() => new Set());
  // the markup and special markup per position in percent, as typed, by
  // index; a position without an entry (or with an empty one) gets none
  const [markups, setMarkups] = useState({});
  const [specialMarkups, setSpecialMarkups] = useState({});
  const defaultMarkupRate = parseRate(config?.general?.defaultMarkupRate) ?? 0;

  // the same thinking effort the panel uses
  const [reasoningEffort] = usePersistedState('aiChat.reasoningEffort', null);

  const purpose = useMemo(
    () => ({ type: POSITION_SCAN_PURPOSE, data: { document } }),
    [document],
  );

  const session = useAiChatSession({
    active: true,
    projectId: project?.id ?? null,
    reasoningEffort,
    purpose,
  });

  const acknowledgement = useAiChatAcknowledgement({ active: true });

  const proposal = useMemo(
    () => latestProposal(session.messages),
    [session.messages],
  );
  const positions = proposal?.positions || [];
  const proposalKey = proposal?.callId ?? null;

  // a new proposal starts with everything selected and loads its pictures
  useEffect(() => {
    if (!proposalKey) return;
    setSelected(allProposed(positions));
    setMarkups({});
    setSpecialMarkups({});
    setImages({});
    let cancelled = false;
    const loaded = {};
    Promise.all(
      proposalImageIds(positions).map((id) =>
        request(config, `files/${id}`, API_GET)
          .then((res) => {
            if (res.data?.id) loaded[id] = res.data;
          })
          // a picture that cannot be loaded is left off the position
          .catch(() => {}),
      ),
    ).then(() => {
      if (!cancelled) setImages(loaded);
    });
    return () => {
      cancelled = true;
    };
    // positions belong to the proposal the key names
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposalKey, config]);

  const { chat, budget } = session;
  const mayWrite = hasPermissions('ai-chat', 'write');
  const canWrite =
    mayWrite && (!chat || String(chat.createdBy) === String(currentUserId));
  const needsAcknowledgement =
    mayWrite && acknowledgement.acknowledged === false;
  // a null grant means the instance has no AI budget at all
  const budgetExhausted =
    Boolean(budget?.available) && budget.usedPercent >= 100;
  const budgetBlocked =
    budget === null || (budget && !budget.available) || budgetExhausted;

  // a 403 "acknowledgement required" means the notice changed: show it again
  const guarded =
    (call) =>
    (...args) =>
      call(...args).catch((err) => {
        if (isAcknowledgementError(err)) acknowledgement.markRequired();
        throw err;
      });
  const send = guarded(session.send);
  const retry = guarded(session.retry);
  const decideApprovals = guarded(session.decideApprovals);

  const isLine = (position) => !positionField(position, 'is_group');
  const selectedPositions = selectedProposed(positions, selected);
  const selectedLines = selectedPositions.filter(isLine).length;
  const totalLines = positions.filter(isLine).length;

  const handleAdd = () => {
    if (!proposal || adding || selectedLines === 0) return;
    setAdding(true);
    try {
      const indexes = positions
        .map((_, index) => index)
        .filter((index) => selected.has(index));
      onAddPositions(
        toDocumentPositions(
          indexes.map((index) => positions[index]),
          {
            defaultTaxRate,
            allowOptional: document === 'offer',
            images,
            markupRates: indexes.map((index) => parseRate(markups[index])),
            specialMarkupRates: indexes.map((index) =>
              parseRate(specialMarkups[index]),
            ),
          },
        ),
      );
    } finally {
      setAdding(false);
    }
  };

  const setMarkup = (index, value) =>
    setMarkups((prev) => ({ ...prev, [index]: value }));
  const setSpecialMarkup = (index, value) =>
    setSpecialMarkups((prev) => ({ ...prev, [index]: value }));
  // the rate for every line that carries a price
  const rateForAllLines = (rate) =>
    Object.fromEntries(
      positions
        .map((position, index) => [index, position])
        .filter(
          ([, position]) =>
            isLine(position) && positionField(position, 'type') !== 'percent',
        )
        .map(([index]) => [index, numberToString(rate, 2)]),
    );
  const applyMarkupToAll = (rate) => setMarkups(rateForAllLines(rate));
  const applySpecialMarkupToAll = (rate) =>
    setSpecialMarkups(rateForAllLines(rate));
  const removeMarkups = () => {
    setMarkups({});
    setSpecialMarkups({});
  };

  // Until the first scan the composer is a "Scan" button for the attached
  // files; afterwards the user can type to ask for changes.
  const started =
    session.messages.length > 0 ||
    Boolean(session.pendingMessage) ||
    session.isBusy;

  // In the review only what the AI said with the proposal and after it
  // matters: its summary, its questions, the user's change requests.
  const reviewMessages = useMemo(() => {
    if (!proposal) return session.messages;
    const index = session.messages.findIndex(
      (m) => m.id === proposal.messageId,
    );
    return index >= 0 ? session.messages.slice(index) : session.messages;
  }, [session.messages, proposal]);

  // the whole dialog is a drop zone for uploads while scanning, like the panel
  const isFileDrag = (e) =>
    Array.from(e.dataTransfer?.types || []).includes('Files');
  const dropZoneProps =
    canWrite && !needsAcknowledgement && !proposal
      ? {
          onDragOver: (e) => {
            if (!isFileDrag(e)) return;
            e.preventDefault();
            e.dataTransfer.dropEffect = 'copy';
            if (!dragActive) setDragActive(true);
          },
          onDragLeave: (e) => {
            if (!e.currentTarget.contains(e.relatedTarget))
              setDragActive(false);
          },
          onDrop: (e) => {
            setDragActive(false);
            if (!e.dataTransfer?.files?.length) return;
            e.preventDefault();
            composerRef.current?.addUploads(e.dataTransfer.files);
          },
        }
      : {};

  return (
    <Box
      className="relative h-full flex flex-col rounded-lg overflow-hidden"
      sx={{
        backgroundColor: colors.glass,
        border: dragActive
          ? `2px dashed ${colors.info}`
          : `1px solid ${colors.selected}`,
      }}
      {...dropZoneProps}
    >
      <Box
        className="flex items-center gap-3 px-4 py-3"
        sx={{ borderBottom: `1px solid ${colors.bgInput}` }}
      >
        <Box
          className="flex items-center justify-center rounded-xl flex-shrink-0"
          sx={{
            width: 40,
            height: 40,
            backgroundColor: `${colors.info}20`,
            color: colors.info,
          }}
        >
          <DocumentScannerIcon />
        </Box>
        <Box className="min-w-0">
          <Typography fontWeight={600}>{t('Scan document with AI')}</Typography>
          <Typography variant="caption" sx={{ opacity: 0.7 }}>
            {proposal
              ? t(
                  'Deselect the positions you do not want, or ask the AI for changes below. Nothing is in the document until you add the selection.',
                )
              : t(
                  'Attach a supplier offer, bill of quantities or price list and start the scan. The AI takes over the positions as they are, asks when something is unclear, and you add them to the document with one click.',
                )}
          </Typography>
          {!proposal && (
            <Typography
              variant="caption"
              className="block"
              sx={{ opacity: 0.7, color: session.isBusy ? colors.info : undefined }}
            >
              {session.isBusy
                ? t('The AI is reading the document. Copying the positions takes about 15 to 20 seconds per page.')
                : t('Copying the positions takes about 15 to 20 seconds per page.')}
            </Typography>
          )}
        </Box>
      </Box>

      {budgetBlocked && (
        <Box className="px-4 pt-3">
          <Box
            className="px-3 py-2 rounded-lg"
            sx={{
              backgroundColor: `${colors.error}15`,
              border: `1px solid ${colors.error}40`,
            }}
          >
            <Typography variant="caption">
              {budgetExhausted
                ? t('The AI budget is used up.')
                : t(
                    'AI is currently unavailable because no AI budget is available.',
                  )}
            </Typography>
          </Box>
        </Box>
      )}

      {needsAcknowledgement ? (
        <AiChatAcknowledgementGate
          saving={acknowledgement.saving}
          onAcknowledge={() =>
            acknowledgement
              .acknowledge()
              .then(session.clearError)
              .catch(() => {})
          }
        />
      ) : (
        <>
          {proposal && (
            <ProposalTable
              positions={positions}
              selected={selected}
              images={images}
              markups={markups}
              specialMarkups={specialMarkups}
              defaultMarkupRate={defaultMarkupRate}
              onMarkupChange={setMarkup}
              onSpecialMarkupChange={setSpecialMarkup}
              onApplyMarkupToAll={applyMarkupToAll}
              onApplySpecialMarkupToAll={applySpecialMarkupToAll}
              onRemoveMarkups={removeMarkups}
              onToggle={(index) =>
                setSelected((prev) => toggleProposed(prev, index, positions))
              }
              onToggleAll={() =>
                setSelected(
                  selected.size === positions.length
                    ? new Set()
                    : allProposed(positions),
                )
              }
            />
          )}

          <Box
            className={
              proposal
                ? 'flex flex-col min-h-0'
                : 'flex-1 min-h-0 flex flex-col'
            }
            sx={
              proposal
                ? { maxHeight: '35%', borderTop: `1px solid ${colors.bgInput}` }
                : undefined
            }
          >
            <AiChatMessageList
              messages={reviewMessages}
              pendingMessage={session.pendingMessage}
              streamingMessage={session.streamingMessage}
              status={chat?.status}
              isBusy={session.isBusy}
              isLoading={session.messagesLoading}
              pageContext={null}
              onRetry={
                canWrite && !budgetBlocked
                  ? () => retry().catch(() => {})
                  : null
              }
              lastError={chat?.lastError}
              pendingApprovals={session.pendingApprovals}
              canDecideApprovals={canWrite}
              onDecideApprovals={decideApprovals}
              emptyState={<ScanEmptyState />}
              contentClassName={CHAT_CONTENT}
            />
          </Box>

          <Box className={CHAT_CONTENT}>
            <AiChatComposer
              ref={composerRef}
              pageContext={null}
              scopeProject={
                project?.id
                  ? {
                      id: project.id,
                      label: project.name || project.title || '',
                    }
                  : null
              }
              sentReferenceKeys={NO_REFERENCES}
              canWrite={canWrite}
              isBusy={session.isBusy}
              blocked={budgetBlocked}
              error={session.error}
              onClearError={session.clearError}
              onSend={send}
              focusKey={started ? 'position-scan' : null}
              fixedPrompt={
                started
                  ? null
                  : t('Scan the attached document and take over its positions.')
              }
              sendLabel={t('Scan')}
              placeholder={
                proposal
                  ? t('Request changes, e.g. "take over only section 2"')
                  : undefined
              }
              />
          </Box>

          {proposal && (
            <Box
              className="flex items-center gap-2 px-4 pb-4 pt-3"
              sx={{ borderTop: `1px solid ${colors.bgInput}` }}
            >
              <Typography
                variant="body2"
                className="flex-1"
                sx={{ opacity: 0.7 }}
              >
                {t('{{count}} of {{total}} positions selected', {
                  count: selectedLines,
                  total: totalLines,
                })}
              </Typography>
              <Button
                color="secondary"
                onClick={onCancel}
                sx={{ textTransform: 'none' }}
              >
                {t('Cancel')}
              </Button>
              <Button
                variant="contained"
                color="success"
                size="large"
                disabled={adding || selectedLines === 0 || session.isBusy}
                onClick={handleAdd}
                startIcon={
                  adding ? <CircularProgress size={16} color="inherit" /> : null
                }
                sx={{ textTransform: 'none', px: 3, fontWeight: 600 }}
              >
                {t('Add {{count}} positions', { count: selectedLines })}
              </Button>
            </Box>
          )}
          <AiChatNoticeLine />
        </>
      )}
    </Box>
  );
};

/** A rate in percent for one row of the review table, as typed. */
const RateInput = ({ value, label, onChange }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const invalid = value !== '' && parseRate(value) === null;

  return (
    <InputBase
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder="–"
      error={invalid}
      inputProps={{
        inputMode: 'decimal',
        'aria-label': label,
        className: 'monospace text-right',
      }}
      endAdornment={
        <Typography variant="caption" sx={{ opacity: 0.6, pl: 0.5 }}>
          %
        </Typography>
      }
      sx={{
        width: 84,
        fontSize: 13,
        px: 1,
        py: 0.25,
        borderRadius: 1,
        backgroundColor: colors.bgInput,
        border: `1px solid ${invalid ? colors.error : 'transparent'}`,
        '&.Mui-focused': { borderColor: colors.info },
      }}
    />
  );
};

/**
 * One rate for every line: an editable rate (starting at initialRate, which
 * may arrive later with the config) and the button that applies it.
 */
const ApplyToAll = ({ label, initialRate, onApply }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();
  // null until the user typed something: the initial rate shows meanwhile
  const [typed, setTyped] = useState(null);
  const value = typed ?? numberToString(initialRate, 2);
  const rate = parseRate(value);

  return (
    <Box
      className="flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-lg"
      sx={{ border: `1px solid ${colors.selected}` }}
    >
      <Typography variant="caption" sx={{ opacity: 0.7 }}>
        {label}
      </Typography>
      <RateInput value={value} label={label} onChange={setTyped} />
      <Button
        size="small"
        color="secondary"
        startIcon={<TrendingUpIcon sx={{ fontSize: 16 }} />}
        disabled={rate === null}
        onClick={() => onApply(rate)}
        sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
      >
        {t('Apply to all')}
      </Button>
    </Box>
  );
};

/**
 * The proposed positions as the model sent them (see positionField), each
 * with a checkbox; groups are bold and indent what belongs to them. The
 * markup and special markup columns take a rate per line (markups and
 * specialMarkups, by index, as typed; both add up); the toolbar above the
 * header applies one rate to every line (the markup starts at the default
 * rate, the special markup at 0) or clears all rates. With
 * a rate the unit price and sum carry it and the document's price is
 * shown underneath.
 */
const ProposalTable = ({
  positions,
  selected,
  images,
  markups,
  specialMarkups,
  defaultMarkupRate,
  onMarkupChange,
  onSpecialMarkupChange,
  onApplyMarkupToAll,
  onApplySpecialMarkupToAll,
  onRemoveMarkups,
  onToggle,
  onToggleAll,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();

  const allSelected = selected.size === positions.length;
  const anyMarkup = [
    ...Object.values(markups),
    ...Object.values(specialMarkups),
  ].some((value) => parseRate(value) !== null);

  return (
    <Box className="flex-1 min-h-0 flex flex-col">
      <Box
        className="flex items-center justify-end gap-2 px-4 py-2"
        sx={{ borderBottom: `1px solid ${colors.bgInput}` }}
      >
        <Typography variant="caption" className="flex-1" sx={{ opacity: 0.7 }}>
          {t(
            'Markups are added on top of the document prices. The document price is kept for every position.',
          )}
        </Typography>
        {anyMarkup && (
          <Button
            size="small"
            color="secondary"
            startIcon={<CloseIcon sx={{ fontSize: 16 }} />}
            onClick={onRemoveMarkups}
            sx={{ textTransform: 'none' }}
          >
            {t('Remove markups')}
          </Button>
        )}
        <ApplyToAll
          label={t('Markup')}
          initialRate={defaultMarkupRate}
          onApply={onApplyMarkupToAll}
        />
        <ApplyToAll
          label={t('Special markup')}
          initialRate={0}
          onApply={onApplySpecialMarkupToAll}
        />
      </Box>
      <Box
        className="flex items-center gap-2 px-4 py-1"
        sx={{ borderBottom: `1px solid ${colors.bgInput}`, opacity: 0.7 }}
      >
        <Checkbox
          size="small"
          checked={allSelected}
          indeterminate={selected.size > 0 && !allSelected}
          onChange={onToggleAll}
          sx={{ p: 0.5 }}
        />
        <Typography variant="caption" className="flex-1">
          {t('Position')}
        </Typography>
        <Typography variant="caption" className="w-32 text-right">
          {t('Amount')}
        </Typography>
        <Typography variant="caption" className="w-28 text-right">
          {t('Unit Price')}
        </Typography>
        <Typography variant="caption" className="w-24 text-right">
          {t('Markup')}
        </Typography>
        <Typography variant="caption" className="w-24 text-right">
          {t('Special markup')}
        </Typography>
        <Typography variant="caption" className="w-28 text-right">
          {t('Sum')}
        </Typography>
      </Box>
      <Box className="flex-1 min-h-0 overflow-y-auto">
        {positions.map((position, index) => {
          const field = (key) => positionField(position, key);
          const isGroup = Boolean(field('is_group'));
          const isPercent = field('type') === 'percent';
          const depth = Math.max(0, parseInt(field('depth'), 10) || 0);
          const amount = Number(field('amount') ?? 1) || 0;
          const documentPrice = Number(field('price')) || 0;
          const markupInput = markups[index] ?? '';
          const specialInput = specialMarkups[index] ?? '';
          const markupRate = isPercent ? null : parseRate(markupInput);
          const specialRate = isPercent ? null : parseRate(specialInput);
          const hasMarkup = markupRate !== null || specialRate !== null;
          const price = hasMarkup
            ? markupPrice(documentPrice, (markupRate ?? 0) + (specialRate ?? 0))
            : documentPrice;
          const image = images[Number(field('image_id'))];
          const checked = selected.has(index);

          return (
            <Box
              key={index}
              onClick={() => onToggle(index)}
              className="flex items-center gap-2 px-4 py-1.5 cursor-pointer select-none"
              sx={{
                borderBottom: `1px solid ${colors.bgInput}`,
                opacity: checked ? 1 : 0.45,
                backgroundColor: isGroup
                  ? `${colors.bgInput}80`
                  : 'transparent',
                '&:hover': { backgroundColor: `${colors.info}10` },
              }}
            >
              <Checkbox
                size="small"
                checked={checked}
                onChange={() => onToggle(index)}
                onClick={(e) => e.stopPropagation()}
                sx={{ p: 0.5, ml: `${depth * 20}px` }}
              />
              {!isGroup && (
                <Box
                  className="flex items-center justify-center rounded flex-shrink-0 overflow-hidden"
                  sx={{
                    width: 32,
                    height: 32,
                    backgroundColor: colors.bgInput,
                  }}
                >
                  {image ? (
                    <img
                      src={image.thumbnailUrl || image.temporaryUrl}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    field('image_id') && (
                      <ImageIcon sx={{ fontSize: 16, opacity: 0.3 }} />
                    )
                  )}
                </Box>
              )}
              <Box className="flex-1 min-w-0">
                <Typography
                  variant="body2"
                  fontWeight={isGroup ? 600 : 500}
                  className="truncate"
                >
                  {field('name')}
                </Typography>
                {!isGroup && field('description') && (
                  <Typography
                    variant="caption"
                    className="block truncate"
                    sx={{ opacity: 0.6 }}
                  >
                    {String(field('description'))
                      .replace(/\s+/g, ' ')
                      .slice(0, 160)}
                  </Typography>
                )}
              </Box>
              {!isGroup && (
                <>
                  <Typography
                    variant="body2"
                    className="w-32 text-right monospace"
                  >
                    {isPercent
                      ? `${numberToString(amount, 2)} %`
                      : `${numberToString(amount, 2)} ${field('unit') || ''}`}
                  </Typography>
                  <Box className="w-28 text-right">
                    <Typography variant="body2" className="monospace">
                      {isPercent ? '' : `${numberToString(price, 2)} €`}
                    </Typography>
                    {hasMarkup && (
                      <Tooltip title={t('Price in the document')}>
                        <Typography
                          variant="caption"
                          className="block monospace"
                          sx={{ opacity: 0.5, textDecoration: 'line-through' }}
                        >
                          {`${numberToString(documentPrice, 2)} €`}
                        </Typography>
                      </Tooltip>
                    )}
                  </Box>
                  <Box
                    className="w-24 flex justify-end"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {!isPercent && (
                      <RateInput
                        value={markupInput}
                        label={t('Markup')}
                        onChange={(value) => onMarkupChange(index, value)}
                      />
                    )}
                  </Box>
                  <Box
                    className="w-24 flex justify-end"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {!isPercent && (
                      <RateInput
                        value={specialInput}
                        label={t('Special markup')}
                        onChange={(value) => onSpecialMarkupChange(index, value)}
                      />
                    )}
                  </Box>
                  <Typography
                    variant="body2"
                    className="w-28 text-right monospace"
                  >
                    {isPercent ? '' : `${numberToString(amount * price, 2)} €`}
                  </Typography>
                </>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

const ScanEmptyState = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();

  return (
    <Box className="flex flex-col items-center text-center gap-2 pt-16 px-4">
      <Box
        className="flex items-center justify-center rounded-2xl mb-2"
        sx={{
          width: 56,
          height: 56,
          backgroundColor: `${colors.info}20`,
          color: colors.info,
        }}
      >
        <DocumentScannerIcon sx={{ fontSize: 30 }} />
      </Box>
      <Typography variant="h4" fontWeight={600}>
        {t('Attach a document to scan')}
      </Typography>
      <Typography variant="body2" sx={{ opacity: 0.6, maxWidth: 360 }}>
        {t(
          'Upload a file, drop it here or pick one from the file explorer or a project folder, then press Scan. Afterwards you can ask for changes, e.g. to take over only certain sections.',
        )}
      </Typography>
    </Box>
  );
};
